/* eslint-disable @typescript-eslint/no-empty-function */
import * as flat from 'flat';
import { WorldMap_worldMap } from 'graphql/query/__generated__/WorldMap';
import { useCoordinatesComputations } from 'hooks/coordinates/useCoordinatesComputations';
import {
    MarkerHistoryRecord,
    ReorderMarkersHistoryRecord,
    UpdateMapRecord
} from 'libs/types/history/historyTypes';
import { DEFAULT_COLOR } from 'libs/types/map/mapTypes';
import { IMarkerDefault } from 'libs/utils/marker/markerTypes';
import { EWorldAction, IFilesToUpload, ISizePosition } from 'libs/utils/world/worldTypes';
import _, { get, isEqual, keys, pick, sortBy, uniq } from 'lodash';
import { createContext, useEffect, useRef, useState } from 'react';
import { ICombinedWorld, IPolygonV2WorldMarker, IWorldMarker } from 'react-give-me-map';
import { IXY } from 'react-give-me-map/dist/utils/map/mapTypes';
import { MapRef } from 'react-map-gl';
import { IEditHistoryActions, IEditHistoryState, useEditHistory } from 'session/useEditHistory';
import { MarkerInCreation, useMarkerCreation } from 'session/useMarkerCreation';
import { PartialDeep } from 'type-fest';
import { v4 as uuid } from 'uuid';
export interface ICtxEdit {
    boundingExtra: IXY;
    worldAction: EWorldAction;
    selectedMarkers: string[];
    mouseDraggingSelection?: ISizePosition;
    refreshHash?: string;
    markerDefault: IMarkerDefault;
    filesToUpload: IFilesToUpload[];
    mapRef?: React.RefObject<MapRef>;
    map: WorldMap_worldMap;
    visibleMapSetIds?: string[];
    worldMarkers: IWorldMarker[];
    editHistory: {
        state: IEditHistoryState;
        actions: IEditHistoryActions;
    };
    markerCreation: {
        marker: IWorldMarker | null;
        initiate: (data: MarkerInCreation) => void;
        finish: (marker: IWorldMarker) => void;
        remove: () => void;
    };
    showInitMapRectangle: IPolygonV2WorldMarker | null;
    markerSidebar: 'properties' | 'interactivity';
    mapWelcomed: boolean;
    setMapWelcomed: (mapWelcomed: boolean) => void;
    setMarkerSidebar: (markerSidebar: 'properties' | 'interactivity') => void;
    setShowInitMapRectangle(marker: IPolygonV2WorldMarker | null);
    setMap: (map: WorldMap_worldMap) => void;
    updateMap: (map: Partial<WorldMap_worldMap>) => void;
    setBoundingExtra: (boundingExtra: IXY) => void;
    setWorldAction: (worldAction: EWorldAction) => void;
    setSelectedMarkers: (selectedMarkers: string[]) => void;
    setMouseDraggingSelection: (mouseDraggingSelection: ISizePosition) => void;
    setRefreshHash: (refreshHash: string) => void;
    setMarkerDefault: (markerDefault: IMarkerDefault) => void;
    setFilesToUpload: (filesToUpload: IFilesToUpload[]) => void;
    setVisibleMapSetIds: (visibleMapSetIds: string[]) => void;
    setWorldMarkers: (worldMarkers: IWorldMarker[]) => void;
    addWorldMarker: (worldMarker: IWorldMarker, skipHistory?: boolean) => void;
    updateWorldMarker: (
        id: string,
        partialMarkerArg: Partial<Omit<IWorldMarker, 'elementData' | 'id'>>,
        partialDataArg?: PartialDeep<ICombinedWorld>
    ) => void;
    removeWorldMarkers: (ids: string[], skipHistory?: boolean) => void;
    updateWorldMarkerData: (id: string, partialData: PartialDeep<ICombinedWorld>) => void;
    setMapRef: (mapRef: React.RefObject<MapRef | null>) => void;
    reorderMarkers: (order: Array<{ worldMapMarkerId: string; order: number }>) => void;
    reset: () => void;
    markerRenderPage: number | null;
    setMarkerRenderPage: (value: number | null) => void;
}

const MARKER_DEFAULT: IMarkerDefault = {
    color: '#' + DEFAULT_COLOR,
    drawType: 'brush' as const,
    drawSize: 10,
    drawFill: false,
    reactCustomMarkerType: '',
    wall: {
        height: 3,
        width: 3,
        color: '#555',
        opacity: 1
    },
    pin: {
        generatedIconUrl:
            'https://dvtqxks0zalev.cloudfront.net/font-icons/Svgs/location-dot-solid.svg',
        imageSize: 0.15,
        textSize: 12,
        generatedBackgroundColor: '#00000000',
        generatedIconColor: '#000'
    }
};

export const CtxEdit = createContext<ICtxEdit>(null);

export const EditStateProvider = ({ children }: { children: React.ReactNode }) => {
    const mapRef = useRef<MapRef | null>(null);
    const [map, setMap] = useState<WorldMap_worldMap>(null);
    const [boundingExtra, setBoundingExtra] = useState<IXY>({
        x: 0,
        y: 0
    });
    const { computeImageBoundaries } = useCoordinatesComputations(mapRef);
    const [worldAction, setWorldAction] = useState<EWorldAction>(EWorldAction.SELECT);
    const [selectedMarkers, setSelectedMarkers] = useState<string[]>([]);
    const [mouseDraggingSelection, setMouseDraggingSelection] = useState<ISizePosition>(null);
    const [refreshHash, setRefreshHash] = useState<string>('');
    const [showInitMapRectangle, setShowInitMapRectangle] = useState<IPolygonV2WorldMarker | null>(
        null
    );
    const [markerDefault, setMarkerDefault] = useState<IMarkerDefault>(MARKER_DEFAULT);
    const [filesToUpload, setFilesToUpload] = useState<IFilesToUpload[]>([]);
    const [worldMarkers, setWorldMarkers] = useState<IWorldMarker[]>([]);
    const [visibleMapSetIds, setVisibleMapSetIds] = useState<string[]>([]);
    const [markerRenderPage, setMarkerRenderPage] = useState(null);
    const markerCreation = useMarkerCreation({
        onMarkerDone: (marker: IWorldMarker | null) => {
            if (marker) {
                addWorldMarker(marker);
                setSelectedMarkers([marker.id]);
            }
            setWorldAction(EWorldAction.SELECT);
        }
    });
    const [markerSidebar, setMarkerSidebar] = useState<'properties' | 'interactivity'>(
        'properties'
    );
    const [mapWelcomed, setMapWelcomed] = useState<boolean>(false);

    function innerAddWorldMarker(worldMarker: IWorldMarker) {
        setWorldMarkers((markers) => [...markers, worldMarker]);
    }

    async function innerUpdateWorldMarker(id: string, newMarker: IWorldMarker) {
        setWorldMarkers((markers) =>
            markers.map((marker) => (marker.id === id ? newMarker : marker))
        );
    }

    function innerRemoveWorldMarkers(ids: string[]) {
        setSelectedMarkers((selectedMarkers) =>
            selectedMarkers.filter((marker) => !_.includes(ids, marker))
        );
        setWorldMarkers((markers) => markers.filter((marker) => !_.includes(ids, marker.id)));
    }

    function innerReorderWorldMarkers(order: Array<{ worldMapMarkerId: string; order: number }>) {
        setWorldMarkers((markers) =>
            markers.map((m) => {
                const foundOrder = order.find((e) => e.worldMapMarkerId === m.id);

                return {
                    ...m,
                    order: foundOrder?.order ?? m.order
                };
            })
        );
    }

    // Terrain must be disabled in the editor
    useEffect(() => {
        if (mapRef?.current?.isStyleLoaded()) {
            mapRef?.current?.getMap().setTerrain(null);
        }
    }, [mapRef?.current, mapRef?.current?.isStyleLoaded()]);

    function innerUpdateMap(newValues: Partial<WorldMap_worldMap>) {
        setMap((curr) => ({ ...curr, ...newValues }));
    }

    const [historyState, historyActions] = useEditHistory({
        addMarker: innerAddWorldMarker,
        updateMarker: innerUpdateWorldMarker,
        removeMarkers: innerRemoveWorldMarkers,
        reorder: innerReorderWorldMarkers,
        updateMap: innerUpdateMap
    });

    function addWorldMarker(worldMarker: IWorldMarker) {
        innerAddWorldMarker(worldMarker);

        // Update history
        const historyValue: MarkerHistoryRecord = {
            recordId: uuid(),
            operation: 'add',
            data: worldMarker
        };
        historyActions.addHistoryRecord(historyValue);
    }

    async function updateWorldMarker(
        id: string,
        partialMarkerArg: Partial<Omit<IWorldMarker, 'elementData' | 'id'>>,
        partialDataArg?: PartialDeep<ICombinedWorld>
    ) {
        const partialMarker = partialMarkerArg ?? {};
        const partialData = partialDataArg ?? {};

        const marker = worldMarkers.find((e) => e.id === id);

        const partialMarkerKeys = Object.keys(partialMarker);
        const partialDataKeys = Object.keys(partialData);

        const noChangeInMarker =
            !partialMarkerKeys.length ||
            partialMarkerKeys.every((key) => get(marker, key) === get(partialMarker, key));
        const noChangeInData =
            !partialDataKeys.length ||
            partialDataKeys.every((key) => get(marker.elementData, key) === get(partialData, key));

        if (noChangeInMarker && noChangeInData) {
            return;
        }

        const newMarker = {
            id: marker.id,
            ...marker,
            ...partialMarker,
            elementData: { ...marker.elementData, ...(partialData ?? {}) }
        } as IWorldMarker;

        if (newMarker.elementType === 'image') {
            newMarker.elementData.layerData = await computeImageBoundaries(newMarker);
        }

        innerUpdateWorldMarker(id, newMarker);

        // Update history
        const historyValue: MarkerHistoryRecord = {
            recordId: uuid(),
            operation: 'update',
            previousData: {
                ...marker
            },
            data: newMarker,
            props: [
                ...Object.keys(partialMarker),
                ...Object.keys(partialData).map((e) => 'elementData.' + e)
            ]
        };

        historyActions.addHistoryRecord(historyValue);
    }

    function reorderMarkers(order: Array<{ worldMapMarkerId: string; order: number }>) {
        const historyValue: ReorderMarkersHistoryRecord = {
            recordId: uuid(),
            operation: 'reorder',
            data: order,
            previousData: worldMarkers.map((m) => ({ worldMapMarkerId: m.id, order: m.order }))
        };

        innerReorderWorldMarkers(order);

        historyActions.addHistoryRecord(historyValue);
    }

    function removeWorldMarkers(ids: string[]) {
        innerRemoveWorldMarkers(ids);

        // Update history
        ids.forEach((id) => {
            const marker = worldMarkers.find((e) => e.id === id);

            const historyValue: MarkerHistoryRecord = {
                recordId: uuid(),
                operation: 'delete',
                data: marker
            };
            historyActions.addHistoryRecord(historyValue);
        });
    }

    function updateWorldMarkerData(id: string, partialData: PartialDeep<ICombinedWorld>) {
        updateWorldMarker(id, {}, partialData);
    }

    //mapbox makes a mapref sometimes a null
    function setMapRef(ref: React.RefObject<MapRef>) {
        if (ref?.current) {
            mapRef.current = ref.current;
        }
    }

    function updateMap(newValues: Partial<WorldMap_worldMap>) {
        const changedKeys = keys(flat(newValues));
        const previousData = pick(map, changedKeys);

        const record: UpdateMapRecord = {
            recordId: uuid(),
            operation: 'updateMap',
            data: newValues,
            previousData
        };

        innerUpdateMap(newValues);

        historyActions.addHistoryRecord(record);
    }

    function reset() {
        console.log('Reset');
        setMap(null);
        setBoundingExtra({
            x: 0,
            y: 0
        });
        setWorldAction(EWorldAction.SELECT);
        setSelectedMarkers([]);
        setMouseDraggingSelection(null);
        setRefreshHash(uuid());
        setMarkerDefault(MARKER_DEFAULT);
        setFilesToUpload([]);
        setWorldMarkers([]);
        setVisibleMapSetIds([]);
    }

    return (
        <CtxEdit.Provider
            value={{
                mapWelcomed,
                setMapWelcomed,
                markerSidebar,
                setMarkerSidebar,
                editHistory: {
                    state: historyState,
                    actions: historyActions
                },
                boundingExtra,
                setBoundingExtra,
                worldAction,
                setWorldAction,
                selectedMarkers: selectedMarkers.filter((id) =>
                    worldMarkers.some((e) => e.id === id)
                ),
                setSelectedMarkers: (markers) => {
                    const uniqueMarkers = uniq(markers);
                    if (!isEqual(sortBy(uniqueMarkers), sortBy(selectedMarkers))) {
                        setSelectedMarkers(markers);
                    }
                },
                updateMap,
                mouseDraggingSelection,
                setMouseDraggingSelection,
                refreshHash,
                setRefreshHash,
                markerDefault,
                setMarkerDefault,
                filesToUpload,
                setFilesToUpload,
                worldMarkers: worldMarkers.filter(
                    (e) => e.id !== markerCreation.markerInCreation?.id
                ),
                setWorldMarkers,
                addWorldMarker,
                updateWorldMarker,
                removeWorldMarkers,
                updateWorldMarkerData,
                map,
                setMap,
                mapRef,
                visibleMapSetIds,
                setVisibleMapSetIds,
                setMapRef,
                reorderMarkers,
                markerCreation: {
                    marker: markerCreation.markerInCreation,
                    initiate: markerCreation.initiateNewMarker,
                    finish: markerCreation.finishNewMarker,
                    remove: markerCreation.removeMarkerCreation
                },
                reset,
                showInitMapRectangle,
                setShowInitMapRectangle,
                markerRenderPage,
                setMarkerRenderPage
            }}
        >
            {children}
        </CtxEdit.Provider>
    );
};
